import { BufferGeometry, Float32BufferAttribute, Group, LineBasicMaterial, LineSegments } from 'three';
import { columnPieces } from '../core/columns';
import type { Aabb, Circle } from '../core/collision';
import type { LevelRun } from '../core/level-runner';
import { playerAabb } from '../core/player';
import { PICKUP_RADIUS, WORLD_TUNING } from '../data/tuning';

/**
 * Debug overlay (issue #53): the exact AABBs and circles of the core drawn as lines, switched on with
 * `?debug=hitbox` or the H key outside production builds. It only reads the core.
 */
const OVERLAY_Z = 0.8;
const CIRCLE_SEGMENTS = 24;

export interface HitboxOverlay {
  readonly group: Group;
  /** Moves the player hitbox to where the plane is drawn this frame. */
  update(player: { x: number; y: number }): void;
  dispose(): void;
}

function boxLines(box: Aabb): number[] {
  const { x, y, halfW, halfH } = box;
  const corners: [number, number][] = [
    [x - halfW, y - halfH],
    [x + halfW, y - halfH],
    [x + halfW, y + halfH],
    [x - halfW, y + halfH],
  ];
  return corners.flatMap(([cx, cy], i) => {
    const [nx, ny] = corners[(i + 1) % 4]!;
    return [cx, cy, OVERLAY_Z, nx, ny, OVERLAY_Z];
  });
}

function circleLines(circle: Circle): number[] {
  const points: number[] = [];
  for (let i = 0; i < CIRCLE_SEGMENTS; i++) {
    const a = (i / CIRCLE_SEGMENTS) * Math.PI * 2;
    const b = ((i + 1) / CIRCLE_SEGMENTS) * Math.PI * 2;
    points.push(
      circle.x + Math.cos(a) * circle.r, circle.y + Math.sin(a) * circle.r, OVERLAY_Z,
      circle.x + Math.cos(b) * circle.r, circle.y + Math.sin(b) * circle.r, OVERLAY_Z,
    );
  }
  return points;
}

export function createHitboxOverlay(run: LevelRun): HitboxOverlay {
  const group = new Group();
  group.name = 'hitbox-overlay';
  const material = new LineBasicMaterial({ color: 0x33ff99 });
  const geometries: BufferGeometry[] = [];

  const add = (name: string, positions: number[]) => {
    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));
    geometries.push(geometry);
    const lines = new LineSegments(geometry, material);
    lines.name = `hitbox:${name}`;
    group.add(lines);
    return lines;
  };

  // The player box is built around the origin and moved with the plane every frame.
  const origin = add('player', boxLines(playerAabb({ x: 0, y: 0 })));

  const width = WORLD_TUNING[run.level.def.world].columnWidth;
  run.level.columns.forEach((column, index) => {
    const [bottom, top] = columnPieces(column, width);
    add(`column:${index}:bottom`, boxLines(bottom));
    add(`column:${index}:top`, boxLines(top));
  });
  run.level.pickups.forEach((pickup, index) => {
    if (pickup.type === 'dollar') add(`dollar:${index}`, circleLines({ x: pickup.x, y: pickup.y, r: PICKUP_RADIUS }));
  });

  return {
    group,
    update(position) {
      origin.position.set(position.x, position.y, 0);
    },
    dispose() {
      for (const geometry of geometries) geometry.dispose();
      material.dispose();
    },
  };
}
